import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getInvoiceByPaymentToken, payInvoiceByToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { 
  FileText, 
  User, 
  Mail, 
  MapPin, 
  Calendar,
  CheckCircle,
  AlertCircle,
  Clock,
  ArrowLeft,
  Loader2,
  CreditCard,
  Building2
} from "lucide-react";

export default function InvoicePayment() {
  const { paymentToken } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  
  const currencySymbols = {
    USD: '$', EUR: '€', GBP: '£', NGN: '₦', KES: 'KSh', 
    GHS: '₵', ZAR: 'R', JPY: '¥', CNY: '¥', INR: '₹'
  };
  
  useEffect(() => {
    fetchInvoice();
  }, [paymentToken]);
  
  const fetchInvoice = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getInvoiceByPaymentToken(paymentToken);
      setInvoice(response.data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        setError("Invoice not found or the payment link is invalid.");
      } else if (status === 410) {
        setError("This payment link has expired. Please request a new link from the invoice creator.");
      } else {
        setError("Failed to load invoice. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handlePayInvoice = async () => {
    if (!user) {
      // Redirect to login with return URL
      toast.info("Please log in to pay this invoice");
      navigate(`/login?return=/invoice/pay/${paymentToken}`);
      return;
    }
    
    try {
      setPaying(true);
      const response = await payInvoiceByToken(paymentToken, {
        payer_email: user.email,
        payer_name: user.full_name || user.email
      });
      
      toast.success("Invoice paid successfully!");
      
      // Refresh invoice to show paid status
      await fetchInvoice();
      
      // Show success and redirect to transactions after delay
      setTimeout(() => {
        navigate('/transactions');
      }, 2000);
      
    } catch (err) {
      const message = err.response?.data?.detail || "Payment failed. Please try again.";
      toast.error(message);
    } finally {
      setPaying(false);
    }
  };
  
  const formatCurrency = (amount, currency) => {
    const symbol = currencySymbols[currency] || currency + ' ';
    return `${symbol}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16162a] to-[#1a1a2e] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#D4AF37] mx-auto mb-4" />
          <p className="text-gray-400">Loading invoice...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16162a] to-[#1a1a2e] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-[#232342]/80 backdrop-blur-xl rounded-2xl border border-red-500/30 p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Unable to Load Invoice</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <Link to="/">
            <Button variant="outline" className="border-gray-600">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go to Homepage
            </Button>
          </Link>
        </div>
      </div>
    );
  }
  
  const isPaid = invoice?.status === 'paid';
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16162a] to-[#1a1a2e] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#B8860B] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">P</span>
            </div>
            <span className="text-white font-semibold text-xl">ParachPay</span>
          </div>
          <p className="text-gray-400 text-sm">Secure Invoice Payment</p>
        </div>
        
        {/* Invoice Card */}
        <div className="bg-[#232342]/80 backdrop-blur-xl rounded-2xl border border-[#D4AF37]/20 overflow-hidden">
          {/* Status Banner */}
          {isPaid ? (
            <div className="bg-emerald-500/20 border-b border-emerald-500/30 px-6 py-4 flex items-center justify-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-400" />
              <span className="text-emerald-400 font-medium">Invoice Paid</span>
            </div>
          ) : (
            <div className="bg-[#D4AF37]/10 border-b border-[#D4AF37]/30 px-6 py-4 flex items-center justify-center gap-2">
              <Clock className="h-5 w-5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] font-medium">Payment Pending</span>
            </div>
          )}
          
          <div className="p-6 space-y-6">
            {/* Invoice Header */}
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="h-5 w-5 text-[#D4AF37]" />
                  <h1 className="text-xl font-semibold text-white">Invoice</h1>
                </div>
                <p className="text-gray-400 text-sm font-mono">#{invoice.invoice_number}</p>
              </div>
              <div className="text-right">
                <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Amount Due</p>
                <p className="text-2xl font-bold text-[#D4AF37]">
                  {formatCurrency(invoice.total, invoice.currency)}
                </p>
              </div>
            </div>
            
            {/* From / To */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* From */}
              <div className="bg-[#1a1a2e]/50 rounded-xl p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">From</p>
                <div className="space-y-2">
                  {invoice.sender?.name && (
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-gray-500" />
                      <span className="text-white">{invoice.sender.name}</span>
                    </div>
                  )}
                  {invoice.sender?.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-500" />
                      <span className="text-gray-400 text-sm">{invoice.sender.email}</span>
                    </div>
                  )}
                  {invoice.sender?.address && (
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-gray-500 mt-0.5" />
                      <span className="text-gray-400 text-sm">{invoice.sender.address}</span>
                    </div>
                  )}
                  {invoice.sender_tag && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-[#D4AF37] text-sm font-medium">@{invoice.sender_tag}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* To */}
              <div className="bg-[#1a1a2e]/50 rounded-xl p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Bill To</p>
                <div className="space-y-2">
                  {invoice.recipient?.name && (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-500" />
                      <span className="text-white">{invoice.recipient.name}</span>
                    </div>
                  )}
                  {invoice.recipient?.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-500" />
                      <span className="text-gray-400 text-sm">{invoice.recipient.email}</span>
                    </div>
                  )}
                  {invoice.recipient?.address && (
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-gray-500 mt-0.5" />
                      <span className="text-gray-400 text-sm">{invoice.recipient.address}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Date */}
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <Calendar className="h-4 w-4" />
              <span>Issued: {formatDate(invoice.created_at)}</span>
            </div>
            
            {/* Line Items */}
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Items</p>
              <div className="bg-[#1a1a2e]/50 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700/50">
                      <th className="text-left text-xs text-gray-500 uppercase p-3">Description</th>
                      <th className="text-center text-xs text-gray-500 uppercase p-3 w-16">Qty</th>
                      <th className="text-right text-xs text-gray-500 uppercase p-3 w-24">Price</th>
                      <th className="text-right text-xs text-gray-500 uppercase p-3 w-28">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.line_items?.map((item, index) => (
                      <tr key={index} className="border-b border-gray-700/30 last:border-b-0">
                        <td className="p-3 text-white text-sm">{item.description}</td>
                        <td className="p-3 text-gray-400 text-sm text-center">{item.quantity}</td>
                        <td className="p-3 text-gray-400 text-sm text-right">
                          {formatCurrency(item.unit_price, invoice.currency)}
                        </td>
                        <td className="p-3 text-white text-sm text-right font-medium">
                          {formatCurrency(item.quantity * item.unit_price, invoice.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Totals */}
            <div className="bg-[#1a1a2e]/50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Subtotal</span>
                <span className="text-white">{formatCurrency(invoice.subtotal, invoice.currency)}</span>
              </div>
              {invoice.tax_rate > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Tax ({invoice.tax_rate}%)</span>
                  <span className="text-white">{formatCurrency(invoice.tax_amount, invoice.currency)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-semibold pt-2 border-t border-gray-700/50">
                <span className="text-white">Total</span>
                <span className="text-[#D4AF37]">{formatCurrency(invoice.total, invoice.currency)}</span>
              </div>
            </div>
            
            {/* Notes */}
            {invoice.notes && (
              <div className="bg-[#1a1a2e]/50 rounded-xl p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Notes</p>
                <p className="text-gray-400 text-sm">{invoice.notes}</p>
              </div>
            )}
            
            {/* Pay Button */}
            {!isPaid && (
              <div className="pt-4">
                <Button
                  onClick={handlePayInvoice}
                  disabled={paying}
                  className="w-full h-14 bg-gradient-to-r from-[#D4AF37] to-[#B8860B] hover:from-[#E5C349] hover:to-[#C99B1D] text-white font-semibold text-lg rounded-xl shadow-lg shadow-[#D4AF37]/20"
                  data-testid="pay-invoice-btn"
                >
                  {paying ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5 mr-2" />
                      Pay {formatCurrency(invoice.total, invoice.currency)}
                    </>
                  )}
                </Button>
                
                {!user && (
                  <p className="text-center text-gray-500 text-sm mt-3">
                    You'll need to log in or create a ParachPay account to pay
                  </p>
                )}
              </div>
            )}
            
            {/* Paid Confirmation */}
            {isPaid && (
              <div className="text-center pt-4">
                <p className="text-emerald-400 mb-4">
                  This invoice has been paid. Thank you!
                </p>
                <Link to="/dashboard">
                  <Button 
                    variant="outline" 
                    className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  >
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <div className="text-center mt-6 text-gray-500 text-sm">
          <p>Powered by <span className="text-[#D4AF37]">ParachPay</span></p>
          <p className="text-xs mt-1">Secure payments worldwide</p>
        </div>
      </div>
    </div>
  );
}
